import { Component, EventEmitter, Inject, Input, OnChanges, OnDestroy, OnInit, Optional, Output, SimpleChanges, ViewChild, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { GridComponent, GridDataResult } from '@progress/kendo-angular-grid';
import { WindowState } from '@progress/kendo-angular-dialog';
import { process, State } from '@progress/kendo-data-query';
import { ReportsService } from '~/services/modules/reports/reports';
import { ThemeManagerService } from '~/services/theme-manager';
import { FormatService } from '~/services/administration.ts/format';
import { LayoutManagerService, PORTAL_DATA } from '~/shared/components/layout/layout-manager';
import { LayoutComponent } from '~/shared/components/layout/layout.component';

export interface ForecastGridItem {
  stockName: string;
  date: string;
  qty: number;
  qtyPendingSO: number;
  qtyPendingPO: number;
  vendor: string;
  estimatedCost: number;
  estimatedPrice: number;
  qtyOnHand: number;
  currentSuggestedReorder: number;
  dataSource: 'Forecast' | 'Historical Data' | string;
}

@Component({
    selector: 'app-report-forecasting',
    templateUrl: './report-forecasting.html',
    styleUrls: ['./report-forecasting.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ReportForecastingComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('forecastGrid') forecastGrid: GridComponent;

  @Input() reportId: number = 0;
  @Input() previewMode: boolean = false;
  @Input() isWindowMode: boolean = false;
  @Input() forecastConfig: any = null;

  @Output() closeForm: EventEmitter<any> = new EventEmitter();

  _active: boolean = true;
  windowState: WindowState = 'default';
  theme: string = 'enterprise';
  loading: boolean = false;
  reportName: string = 'Forecast Report';

  // Core Data
  rawData: any[] = [];
  normalizedData: ForecastGridItem[] = [];
  filteredData: ForecastGridItem[] = [];
  gridView: GridDataResult = { data: [], total: 0 };

  // View Mode: 'grid' (default) vs 'chart'
  showChart: boolean = false;

  // Filter Bar Toggles
  filterForecast: boolean = true;
  filterHistorical: boolean = true;
  useSystemDecimals: boolean = false;
  hideZeroQtyForecast: boolean = false;

  // Kendo Grid State (Sorting, Paging, Grouping)
  gridState: State = {
    skip: 0,
    take: 50,
    sort: [],
    group: []
  };

  // Aggregates for Footers
  totalEstimatedCost: number = 0;
  totalEstimatedPrice: number = 0;

  // Chart Data
  chartCategories: string[] = [];
  chartSeries: { name: string; data: number[]; color?: string }[] = [];

  constructor(
    private reportsService: ReportsService,
    private themeManager: ThemeManagerService,
    private formatService: FormatService,
    private layoutManager: LayoutManagerService,
    @Optional() private layoutComponent: LayoutComponent,
    @Optional() @Inject(PORTAL_DATA) public data: any
  ) {
    this.themeManager.themeChange.subscribe((t) => {
      this.theme = t || 'enterprise';
    });

    if (this.data?.model) {
      this.reportId = this.data.model.reportId ?? this.reportId;
      this.previewMode = this.data.model.previewMode ?? this.previewMode;
      this.isWindowMode = true;
    }
  }

  ngOnInit(): void {
    if (this.isWindowMode) {
      this.layoutManager.applyOverly();
    }
    if (this.reportId > 0) {
      this.loadForecast();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.reportId && !changes.reportId.firstChange) {
      if (this.reportId > 0) {
        this.loadForecast();
      }
    }
  }

  loadForecast(): void {
    if (!this.reportId || this.reportId <= 0) return;

    this.loading = true;
    this.reportsService.ReportById(this.reportId).subscribe({
      next: (report: any) => {
        if (report) {
          this.reportName = report.name || 'Forecast Report';
          let config: any = {};
          if (report.forecastConfig) {
            if (typeof report.forecastConfig === 'string') {
              try {
                config = JSON.parse(report.forecastConfig);
              } catch (e) {
                config = {};
              }
            } else {
              config = report.forecastConfig;
            }
          }
          this.executeForecastProcess(config);
        } else {
          this.loading = false;
        }
      },
      error: (err) => {
        console.error('Failed to load forecast report definition:', err);
        this.loading = false;
      }
    });
  }

  executeForecastProcess(config: any): void {
    this.reportsService.ProcessForecast(config).subscribe({
      next: (res: any) => {
        let items: any[] = [];
        if (Array.isArray(res)) {
          items = res;
        } else if (res?.data && Array.isArray(res.data)) {
          items = res.data;
        } else if (res?.Data && Array.isArray(res.Data)) {
          items = res.Data;
        }

        this.rawData = items;
        this.normalizedData = items.map((row) => this.normalizeRow(row));
        this.applyFilters();
        this.loading = false;
      },
      error: (err) => {
        console.error('Failed to process forecast:', err);
        this.loading = false;
      }
    });
  }

  normalizeRow(row: any): ForecastGridItem {
    const stockName = row.stocknumber ?? row.StockNumber ?? row.stockNumber ?? row.stockid ?? row.StockId ?? row.StockName ?? row.stockName ?? row.ItemName ?? row.itemName ?? row.ItemNumber ?? row.itemNumber ?? 'N/A';
    
    const rawDate = row.historydate ?? row.HistoryDate ?? row.forecastdate ?? row.ForecastDate ?? row.Date ?? row.date ?? row.Month ?? row.month ?? '';
    const date = this.formatDateValue(rawDate);

    const qty = Number(row.qty ?? row.Qty ?? row.Quantity ?? row.quantity ?? row.forecastQty ?? row.historicalQty ?? 0);
    const qtyPendingSO = Number(row.QtyPendingOnSO ?? row.qtyPendingOnSO ?? row.QtyPendingSO ?? row.qtyPendingSO ?? row.qtypendingso ?? row.PendingSO ?? row.pendingSO ?? 0);
    const qtyPendingPO = Number(row.QtyPendingOnPO ?? row.qtyPendingOnPO ?? row.QtyPendingPO ?? row.qtyPendingPO ?? row.qtypendingpo ?? row.PendingPO ?? row.pendingPO ?? 0);
    
    const vendor = row.vendorcode ?? row.VendorCode ?? row.customercode ?? row.CustomerCode ?? row.Vendor ?? row.vendor ?? row.VendorName ?? row.vendorName ?? 'All Vendors';
    
    const estimatedCost = Number(row.EstimatedCost ?? row.estimatedCost ?? row.Cost ?? row.cost ?? 0);
    const estimatedPrice = Number(row.EstimatedPrice ?? row.estimatedPrice ?? row.Price ?? row.price ?? (estimatedCost > 0 ? estimatedCost * 1.25 : 0));
    
    const qtyOnHand = Number(row.CurrentlyInStock ?? row.currentlyInStock ?? row.QtyOnHand ?? row.qtyOnHand ?? row.OnHand ?? row.onHand ?? 0);
    
    let currentSuggestedReorder = 0;
    if (row.CurrentSuggestedReorder != null) {
      currentSuggestedReorder = Number(row.CurrentSuggestedReorder);
    } else if (row.currentSuggestedReorder != null) {
      currentSuggestedReorder = Number(row.currentSuggestedReorder);
    } else if (row.suggestedReorder != null) {
      currentSuggestedReorder = Number(row.suggestedReorder);
    } else if (row.Variance != null) {
      currentSuggestedReorder = Number(row.Variance);
    } else if (row.variance != null) {
      currentSuggestedReorder = Number(row.variance);
    } else if (row.ConditionResult != null) {
      currentSuggestedReorder = Number(row.ConditionResult);
    }

    const isForecast = row.forecasted === true || row.isForecast === true || (typeof row.DataSource === 'string' && row.DataSource.toLowerCase().includes('forecast')) || (typeof row.dataSource === 'string' && row.dataSource.toLowerCase().includes('forecast'));
    const dataSource = isForecast ? 'Forecast' : 'Historical Data';

    return {
      stockName,
      date,
      qty,
      qtyPendingSO,
      qtyPendingPO,
      vendor,
      estimatedCost,
      estimatedPrice,
      qtyOnHand,
      currentSuggestedReorder,
      dataSource
    };
  }

  formatDateValue(rawDate: any): string {
    if (!rawDate) return '';
    if (typeof rawDate === 'string' && rawDate.includes('-') && !rawDate.includes('T') && !rawDate.includes(':') && isNaN(Number(rawDate.substring(0, 4)))) {
      return rawDate;
    }
    try {
      const d = new Date(rawDate);
      if (!isNaN(d.getTime())) {
        const monthNames = [
          'January', 'February', 'March', 'April', 'May', 'June',
          'July', 'August', 'September', 'October', 'November', 'December'
        ];
        return `${monthNames[d.getMonth()]}-${d.getFullYear()}`;
      }
    } catch (e) {}
    return String(rawDate);
  }

  applyFilters(): void {
    if (!this.normalizedData || !this.normalizedData.length) {
      this.filteredData = [];
      this.gridView = process([], this.gridState);
      this.calculateTotals();
      this.buildChart();
      return;
    }

    this.filteredData = this.normalizedData.filter((item) => {
      const isForecast = (item.dataSource || '').toLowerCase().includes('forecast');
      const isHistorical = (item.dataSource || '').toLowerCase().includes('historical') || !isForecast;

      if (isForecast && !this.filterForecast) {
        return false;
      }
      if (isHistorical && !this.filterHistorical) {
        return false;
      }
      if (this.hideZeroQtyForecast && isForecast && item.qty === 0) {
        return false;
      }
      return true;
    });

    this.gridView = process(this.filteredData, this.gridState);
    this.calculateTotals();
    this.buildChart();
  }

  onGridStateChange(state: State): void {
    this.gridState = state;
    this.gridView = process(this.filteredData, this.gridState);
  }

  calculateTotals(): void {
    this.totalEstimatedCost = this.filteredData.reduce((sum, item) => sum + (item.estimatedCost || 0), 0);
    this.totalEstimatedPrice = this.filteredData.reduce((sum, item) => sum + (item.estimatedPrice || 0), 0);
  }

  toggleChart(): void {
    this.showChart = !this.showChart;
  }

  exportToExcel(): void {
    if (this.forecastGrid) {
      this.forecastGrid.saveAsExcel();
    }
  }

  buildChart(): void {
    if (!this.filteredData || !this.filteredData.length) {
      this.chartCategories = [];
      this.chartSeries = [];
      return;
    }

    const dateMap = new Map<string, { forecast: number; historical: number; onHand: number }>();
    this.filteredData.forEach((item) => {
      const d = item.date || 'Unknown';
      if (!dateMap.has(d)) {
        dateMap.set(d, { forecast: 0, historical: 0, onHand: 0 });
      }
      const bucket = dateMap.get(d)!;
      if ((item.dataSource || '').toLowerCase().includes('forecast')) {
        bucket.forecast += item.qty;
      } else {
        bucket.historical += item.qty;
      }
      bucket.onHand += item.qtyOnHand;
    });

    const categories = Array.from(dateMap.keys());
    const forecastData: number[] = [];
    const historicalData: number[] = [];
    const onHandData: number[] = [];

    categories.forEach((cat) => {
      const bucket = dateMap.get(cat)!;
      forecastData.push(bucket.forecast);
      historicalData.push(bucket.historical);
      onHandData.push(bucket.onHand);
    });

    this.chartCategories = categories;
    this.chartSeries = [
      { name: 'Forecast Qty', data: forecastData, color: '#2563eb' },
      { name: 'Historical Qty', data: historicalData, color: '#059669' },
      { name: 'Qty On Hand', data: onHandData, color: '#d97706' }
    ];
  }

  onClose(): void {
    this._active = false;
    if (this.isWindowMode) {
      this.layoutManager.removeOverlay();
      this.layoutComponent?.detachDialogForm();
    }
    this.closeForm.emit();
  }

  ngOnDestroy(): void {
    if (this.isWindowMode) {
      this.layoutManager.removeOverlay();
      this.layoutComponent?.detachDialogForm();
    }
  }

  onStateChanges(e: any): void {
    if (e === 'minimized') {
      this.layoutManager.removeOverlay();
    } else {
      this.layoutManager.applyOverly();
    }
  }
}
